/**
 * Geld wird überall als ganze Cent (Integer) geführt, nie als Fließkommazahl.
 * Rundung: kaufmännisch (0,5 rundet auf), bei negativen Beträgen symmetrisch.
 * Berechnungen, die die sichere Ganzzahlgrenze überschreiten würden, werfen einen Fehler.
 */

export function assertCents(value: number): void {
  if (!Number.isSafeInteger(value)) {
    throw new TypeError(`Ein Geldbetrag muss ganze Cent sein, erhalten: ${String(value)}`);
  }
}

function assertSafeProduct(value: number): number {
  if (!Number.isSafeInteger(value)) {
    throw new RangeError('Der Betrag ist zu groß für eine sichere Berechnung.');
  }
  return value;
}

const formatters = new Map<string, Intl.NumberFormat>();

/** Formatiert Cent als Euro, z. B. 1234 -> „12,34 €“ (mit geschütztem Leerzeichen). */
export function formatEuro(cents: number, locale = 'de-DE'): string {
  assertCents(cents);
  let formatter = formatters.get(locale);
  if (formatter === undefined) {
    formatter = new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' });
    formatters.set(locale, formatter);
  }
  return formatter.format(cents / 100);
}

/**
 * Betrag ohne Währungszeichen und ohne Tausendertrenner mit Punkt und genau zwei Nachkommastellen, z. B. 1230 -> „12.30“
 * (GiroCode, SEPA-Datei). Rein ganzzahlig aus Cent, nie über Fließkomma. Negative Beträge ergeben „-12.30“.
 */
export function formatEuroPlain(cents: number): string {
  assertCents(cents);
  const sign = cents < 0 ? '-' : '';
  const absolute = Math.abs(cents);
  const whole = Math.floor(absolute / 100);
  const fraction = absolute % 100;
  return `${sign}${String(whole)}.${String(fraction).padStart(2, '0')}`;
}

/**
 * Liest einen Betrag von Hand. Erlaubt sind nur eindeutige Schreibweisen:
 * „12“, „4,5“, „4,50“, „4.50“ sowie deutsche Tausendergruppen „1.234,56“ und „1.234.567“.
 * Alles Mehrdeutige („1.234“, „1,234“, „4..50“, „4 50“, „1.2.3“) liefert `null`,
 * damit sich kein Tippfehler unbemerkt um den Faktor 100 verrechnet.
 */
export function parseEuro(input: string): number | null {
  const text = input.replace(/€/g, '').trim();
  let normalized: string;
  if (/^\d{1,3}(\.\d{3}){2,}(,\d{1,2})?$/.test(text) || /^\d{1,3}(\.\d{3})+,\d{1,2}$/.test(text)) {
    normalized = text.replace(/\./g, '').replace(',', '.');
  } else if (/^\d+([.,]\d{1,2})?$/.test(text)) {
    normalized = text.replace(',', '.');
  } else {
    return null;
  }
  const [whole = '0', fraction = ''] = normalized.split('.');
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return Number.isSafeInteger(cents) ? cents : null;
}

/** Höchstwert für Prozentangaben in Basispunkten (100 % = 10000 bp). */
const MAX_BASIS_POINTS = 10_000;

/**
 * Höchstwert der Provision in Basispunkten (50 %). Muss mit der Grenze der API übereinstimmen
 * (`apps/api/src/events/eventRoutes.ts`, `commissionBasisPoints`).
 */
const MAX_COMMISSION_BASIS_POINTS = 5_000;

/**
 * Liest einen Prozentwert von Hand und liefert Basispunkte (100 bp = 1 %) als ganze Zahl.
 * Erlaubt sind „12“, „12,5“, „12.5“, „12,50“ sowie jeweils mit „ %“ dahinter, von 0 bis 50 %
 * (Obergrenze der Provision laut API).
 * Negative Werte, mehr als zwei Nachkommastellen, Werte über 50 % und alles andere liefern `null`.
 * Gerechnet wird ganzzahlig: der Text wird in Ganzzahl-Teile zerlegt, nie per Fließkomma.
 */
export function parsePercentToBasisPoints(input: string): number | null {
  const text = input.trim().replace(/\s*%$/, '');
  const match = /^(\d+)(?:[.,](\d{1,2}))?$/.exec(text);
  if (match === null) {
    return null;
  }
  const [, wholeText = '', fractionText = ''] = match;
  const whole = wholeText.replace(/^0+(?=\d)/, '');
  if (whole.length > 3) {
    return null;
  }
  const basisPoints = Number(whole) * 100 + Number(fractionText.padEnd(2, '0'));
  return basisPoints <= MAX_COMMISSION_BASIS_POINTS ? basisPoints : null;
}

/**
 * Formatiert Basispunkte als deutschen Prozentwert, z. B. 1250 -> „12,5 %“, 1205 -> „12,05 %“.
 * Wirft einen Fehler für Werte außerhalb von 0 bis 10000 bp.
 */
export function formatBasisPointsAsPercent(basisPoints: number): string {
  if (!Number.isSafeInteger(basisPoints) || basisPoints < 0 || basisPoints > MAX_BASIS_POINTS) {
    throw new RangeError(
      `Basispunkte müssen ganze Zahlen von 0 bis 10000 sein, erhalten: ${String(basisPoints)}`,
    );
  }
  const whole = Math.floor(basisPoints / 100);
  const fraction = basisPoints % 100;
  if (fraction === 0) {
    return `${String(whole)} %`;
  }
  if (fraction % 10 === 0) {
    return `${String(whole)},${String(fraction / 10)} %`;
  }
  return `${String(whole)},${String(fraction).padStart(2, '0')} %`;
}

export function sumCents(values: readonly number[]): number {
  let total = 0;
  for (const value of values) {
    assertCents(value);
    total = assertSafeProduct(total + value);
  }
  return total;
}

/** Normalisiert -0 zu 0. */
function noNegativeZero(value: number): number {
  return value === 0 ? 0 : value;
}

/**
 * Prozentanteil in Basispunkten (100 bp = 1 %), z. B. 2000 bp = 20 %.
 * Ganzzahlig gerechnet, kaufmännisch gerundet.
 */
export function percentOfCents(cents: number, basisPoints: number): number {
  assertCents(cents);
  if (!Number.isSafeInteger(basisPoints) || basisPoints < 0) {
    throw new TypeError('basisPoints muss eine nicht negative ganze Zahl sein.');
  }
  const sign = cents < 0 ? -1 : 1;
  const absolute = Math.abs(cents);
  const scaled = assertSafeProduct(absolute * basisPoints + 5000);
  return noNegativeZero(sign * Math.floor(scaled / 10000));
}

/**
 * Zerlegt einen Bruttobetrag in Netto und Umsatzsteuer (Steuersatz als ganze Prozent, z. B. 19).
 * Netto wird gerundet, die Steuer ist der Rest, damit Netto + USt immer genau Brutto ergibt.
 */
export function splitGross(
  grossCents: number,
  vatPercent: number,
): { netCents: number; vatCents: number; grossCents: number } {
  assertCents(grossCents);
  if (!Number.isSafeInteger(vatPercent) || vatPercent < 0) {
    throw new TypeError('vatPercent muss eine nicht negative ganze Zahl sein (z. B. 19).');
  }
  const sign = grossCents < 0 ? -1 : 1;
  const absolute = Math.abs(grossCents);
  const denominator = 100 + vatPercent;
  // Rundung half-up rein in Ganzzahlen: floor((2 * x * 100 + d) / (2 * d)) = round(x * 100 / d)
  const scaled = assertSafeProduct(absolute * 200 + denominator);
  const net = Math.floor(scaled / (2 * denominator));
  return {
    netCents: noNegativeZero(sign * net),
    vatCents: noNegativeZero(sign * (absolute - net)),
    grossCents,
  };
}

/**
 * Durchschnitt in ganzen Cent (kaufmännisch gerundet, 0,5 rundet auf), `null` bei 0 Stück.
 * Gerechnet wird ganzzahlig aus Summe und Anzahl, nie aus einem Fließkomma-Mittelwert.
 */
export function averageCents(sumCents: number, count: number): number | null {
  assertCents(sumCents);
  if (!Number.isSafeInteger(count) || count < 0) {
    throw new TypeError('Die Anzahl muss eine nicht negative ganze Zahl sein.');
  }
  if (count === 0) return null;
  const sign = sumCents < 0 ? -1 : 1;
  const scaled = assertSafeProduct(Math.abs(sumCents) * 2 + count);
  return noNegativeZero(sign * Math.floor(scaled / (2 * count)));
}

/**
 * Anteil in ganzen Basispunkten (100 bp = 1 %, kaufmännisch gerundet), `null` bei Nenner 0.
 * Beispiel: 2 von 3 = 6667 bp.
 */
export function ratioBasisPoints(part: number, whole: number): number | null {
  if (!Number.isSafeInteger(part) || !Number.isSafeInteger(whole) || part < 0 || whole < 0) {
    throw new TypeError('Anteil und Gesamtzahl müssen nicht negative ganze Zahlen sein.');
  }
  if (whole === 0) return null;
  const scaled = assertSafeProduct(part * 20_000 + whole);
  return Math.floor(scaled / (2 * whole));
}
